/* eslint-disable camelcase */
/* Código de tributação nacional (cTribNac) com sugestões.
   Abre com o que a empresa já usou (o código do mesmo tomador na frente) e as opções dos
   CNAEs dela. Conforme a pessoa digita o código ("17.19") ou parte da descrição
   ("contabilidade"), traz também a Lista de Serviços Nacional. O formulário guarda só os
   6 dígitos, que é o que vai na DPS. */
import React, { useMemo, useState } from 'react';
import { Box, FormControl, FormErrorMessage, FormLabel, Text } from '@chakra-ui/react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AsyncSelect } from 'chakra-react-select';
import _ from 'lodash';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useNfseEmissao } from '../../../../../hooks/useNfseEmissao';
import { INfseServicoSugestao } from '../../../../../types/nfseEmissao';
import { extractErrorMessage } from '../../../../../utils/ErrorUtils';
import { propsMenuSelect } from '../../../../../utils/selectMenu';
import { C } from './ui';

interface Opcao {
  value: string;
  label: string;
  servico: INfseServicoSugestao;
}

interface Grupo {
  label: string;
  options: Opcao[];
}

const GRUPOS: Record<INfseServicoSugestao['origem'], string> = {
  tomador: 'Usado para este tomador',
  historico: 'Usados pela empresa',
  cnae: 'Pelo CNAE da empresa',
  catalogo: 'Lista de Serviços Nacional',
};

export const formatarCTribNac = (c: string) =>
  c.length === 6 ? `${c.slice(0, 2)}.${c.slice(2, 4)}.${c.slice(4)}` : c;

const opcao = (s: INfseServicoSugestao): Opcao => ({
  value: s.codigo,
  label: `${formatarCTribNac(s.codigo)} · ${s.descricao}`,
  servico: s,
});

const agrupar = (lista: INfseServicoSugestao[]): Grupo[] =>
  (Object.keys(GRUPOS) as INfseServicoSugestao['origem'][])
    .map(origem => ({
      label: GRUPOS[origem],
      options: lista.filter(s => s.origem === origem).map(opcao),
    }))
    .filter(g => g.options.length);

// Código de 6 dígitos que não veio na lista: a pessoa ainda pode usá-lo.
const avulso = (codigo: string): Opcao =>
  opcao({
    codigo,
    descricao: 'código digitado',
    origem: 'catalogo',
    no_catalogo: false,
    usos: 0,
  });

export function ServicoBusca({
  name,
  label,
  emitenteId,
  isRequired,
  isDisabled,
  helper,
  helperColor,
  onTrocar,
}: {
  name: string;
  label: string;
  emitenteId?: number | null;
  isRequired?: boolean;
  isDisabled?: boolean;
  helper?: string;
  helperColor?: string;
  // A pessoa trocou o código: o que dependia do serviço antigo precisa ser revisto.
  onTrocar?: () => void;
}) {
  const { buscarServicos } = useNfseEmissao();
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const error = _.get(errors, name);
  const codigo = (useWatch({ name }) as string) ?? '';
  const tomador = ((useWatch({ name: 'tomador_doc' }) as string) ?? '').replace(/\D/g, '');
  const tomadorCompleto = tomador.length === 11 || tomador.length === 14 ? tomador : '';
  const [texto, setTexto] = useState('');
  const [escolhido, setEscolhido] = useState<INfseServicoSugestao | null>(null);

  // Lista que abre ao clicar: o histórico da empresa, refeito quando o tomador muda.
  const iniciais = useQuery({
    queryKey: ['nfse-servicos', emitenteId as number, tomadorCompleto],
    queryFn: () => buscarServicos(emitenteId as number, undefined, tomadorCompleto),
    enabled: !!emitenteId,
    staleTime: 5 * 60 * 1000,
    // Ao trocar de tomador, o código escolhido não pisca como "código digitado".
    placeholderData: keepPreviousData,
  });

  // Código já no formulário (padrão, rascunho, duplicada) que não está no histórico.
  const conhecido =
    iniciais.data?.find(s => s.codigo === codigo) ??
    (escolhido?.codigo === codigo ? escolhido : null);
  const doCodigo = useQuery({
    queryKey: ['nfse-servico', emitenteId as number, codigo],
    queryFn: () => buscarServicos(emitenteId as number, codigo),
    enabled: !!emitenteId && /^\d{6}$/.test(codigo) && !conhecido && iniciais.isFetched,
    staleTime: Infinity,
  });
  const atual = conhecido ?? doCodigo.data?.find(s => s.codigo === codigo) ?? null;
  let selecionado: Opcao | null = null;
  if (atual) selecionado = opcao(atual);
  else if (codigo) selecionado = avulso(codigo);

  const carregar = useMemo(() => {
    let timer: ReturnType<typeof setTimeout>;
    return (termo: string): Promise<Grupo[]> =>
      new Promise(resolve => {
        clearTimeout(timer);
        if (!emitenteId || !termo.trim()) {
          resolve(agrupar(iniciais.data ?? []));
          return;
        }
        timer = setTimeout(() => {
          buscarServicos(emitenteId, termo, tomadorCompleto)
            .then(lista => {
              const grupos = agrupar(lista);
              const digitos = termo.replace(/\D/g, '');
              if (digitos.length === 6 && !lista.some(s => s.codigo === digitos))
                grupos.push({ label: 'Usar o código digitado', options: [avulso(digitos)] });
              resolve(grupos);
            })
            .catch(() => resolve([]));
        }, 200);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emitenteId, tomadorCompleto, iniciais.data]);

  // O aviso de conferência vem antes da marca do campo: "editado" não pode escondê-lo.
  let aviso = '';
  if (atual?.no_catalogo === false || (codigo && !atual && doCodigo.isSuccess))
    aviso = `${formatarCTribNac(codigo)} não está na Lista de Serviços Nacional: confira`;
  else if (atual?.origem === 'tomador') aviso = 'Mesmo código da última nota para este tomador';
  else if (atual?.origem === 'cnae')
    aviso = `Possível pelo CNAE ${atual.cnae}: confira se é o serviço da nota`;
  else if (atual?.origem === 'historico')
    aviso = `Pelo histórico: usado em ${atual.usos} nota${atual.usos === 1 ? '' : 's'}`;
  const ajuda =
    [aviso, helper].filter(Boolean).join(' · ') || 'Digite o código ou parte da descrição';

  return (
    <FormControl isRequired={isRequired} isInvalid={!!error} mb={5}>
      <FormLabel fontWeight="bold" mb={1} htmlFor={`${name}-busca`}>
        {label}
      </FormLabel>
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, onBlur } }) => (
          <AsyncSelect<Opcao, false, Grupo>
            inputId={`${name}-busca`}
            placeholder="Código ou descrição do serviço"
            loadOptions={carregar}
            // O histórico aparece já no clique, sem digitar nada.
            defaultOptions={agrupar(iniciais.data ?? [])}
            cacheOptions={false}
            inputValue={texto}
            onInputChange={(novo, { action }) => {
              if (action === 'input-change') setTexto(novo);
              if (action === 'input-blur' || action === 'set-value') setTexto('');
            }}
            value={selecionado}
            onChange={item => {
              const s = (item as Opcao | null)?.servico ?? null;
              setEscolhido(s);
              onChange(s?.codigo ?? '');
              onTrocar?.();
              // Descrição vazia ganha a da última nota com esse código; digitada, fica.
              if (s?.ultima_descricao && !getValues('descricao'))
                setValue('descricao', s.ultima_descricao, { shouldDirty: true });
            }}
            onBlur={onBlur}
            formatOptionLabel={(o, { context }) =>
              context === 'value' ? (
                o.label
              ) : (
                <Box>
                  <Text as="span" fontWeight="600">
                    {formatarCTribNac(o.value)}
                  </Text>{' '}
                  <Text as="span">{o.servico.descricao}</Text>
                  {o.servico.origem === 'cnae' && (
                    <Text fontSize="12px" color={C.subtle}>
                      CNAE {o.servico.cnae}
                      {o.servico.usos > 0
                        ? ` · ${o.servico.usos} nota${o.servico.usos === 1 ? '' : 's'} na rede`
                        : ''}
                    </Text>
                  )}
                  {o.servico.origem !== 'cnae' && o.servico.usos > 0 && (
                    <Text fontSize="12px" color={C.subtle}>
                      {o.servico.usos} nota{o.servico.usos === 1 ? '' : 's'}
                      {o.servico.ultima_descricao
                        ? ` · "${o.servico.ultima_descricao.slice(0, 80)}"`
                        : ''}
                    </Text>
                  )}
                </Box>
              )
            }
            loadingMessage={() => 'Buscando...'}
            noOptionsMessage={() => 'Nenhum serviço com esse código ou descrição'}
            isClearable
            isDisabled={isDisabled || !emitenteId}
            isInvalid={!!error}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
            {...propsMenuSelect}
          />
        )}
      />
      {!error && (
        <Text fontSize="12px" mt="6px" color={helperColor ?? C.subtle}>
          {ajuda}
        </Text>
      )}
      <FormErrorMessage>{extractErrorMessage(error?.message)}</FormErrorMessage>
    </FormControl>
  );
}
